import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Building2, Plus, Pencil, Trash2, X, Loader2, Star, Users, ShieldCheck, MapPin,
  FileText, Image as ImageIcon, Trash,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { pedirExclusao } from '@/lib/confirmarExclusao'

interface Filial {
  id: number
  empresa_id: number
  codigo: string
  razao_social: string
  nome_fantasia: string
  cnpj: string | null
  ie: string | null
  im: string | null
  is_matriz: boolean
  ativo: boolean
  somente_fiscal: boolean
  perfil_operacao: string   // 'loja' | 'industria' — como o kit sai na NF-e
  cep: string | null
  endereco: string | null
  numero: string | null
  complemento: string | null
  bairro: string | null
  cidade: string | null
  uf: string | null
  telefone: string | null
  email: string | null
  regime_tributario: string | null
  crt: string | null
  ambiente_nfe: string | null
  serie_nfe: string | null
  serie_nfce: string | null
  csc_id: string | null
  csc_token: string | null
  nfe_proxima_numero: number | null
}

const VAZIA: Partial<Filial> = {
  codigo: '', razao_social: '', nome_fantasia: '', cnpj: '', ie: '',
  is_matriz: false, ativo: true, somente_fiscal: false, perfil_operacao: 'loja',
  ambiente_nfe: '2', serie_nfe: '1', serie_nfce: '1',
}

// Toggle compacto reutilizado na lista de filiais (ativa / apenas fiscal).
function MiniToggle({ on, onChange, cor = 'emerald', title }: {
  on: boolean; onChange: () => void; cor?: 'emerald' | 'brand'; title?: string
}) {
  const bgOn = cor === 'brand' ? 'bg-brand-600' : 'bg-emerald-500'
  return (
    <button type="button" title={title}
      onClick={(e) => { e.stopPropagation(); onChange() }}
      className={`inline-flex w-9 h-5 rounded-full p-0.5 transition ${on ? bgOn : 'bg-ink-300'}`}>
      <div className={`w-4 h-4 rounded-full bg-white transition-transform ${on ? 'translate-x-4' : ''}`} />
    </button>
  )
}

export default function FiliaisPage() {
  const qc = useQueryClient()
  const [editing, setEditing] = useState<Partial<Filial> | null>(null)
  const [tab, setTab] = useState<'geral' | 'fiscal' | 'usuarios'>('geral')

  const { data: filiais = [], isLoading } = useQuery<Filial[]>({
    queryKey: ['filiais-admin'],
    // gestão vê/edita TODAS: também as "somente_fiscal" e as INATIVAS (que ficam
    // escondidas no seletor de filial e nos relatórios). Toggles reativam/marcam na lista.
    queryFn: async () => (await api.get('/filiais', { params: { incluir_fiscais: true, incluir_inativas: true } })).data,
  })

  const salvar = useMutation({
    mutationFn: async (f: Partial<Filial>) => {
      const payload = { ...f }
      delete (payload as any).id
      delete (payload as any).empresa_id
      if (f.id) return (await api.put(`/filiais/${f.id}`, payload)).data
      return (await api.post('/filiais', payload)).data
    },
    onSuccess: (saved) => {
      qc.invalidateQueries({ queryKey: ['filiais-admin'] })
      qc.invalidateQueries({ queryKey: ['filiais-minhas'] })
      toast.success('Filial salva')
      if (!editing?.id) setEditing(saved)
      else setEditing(null)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })

  // 30/06: toggle direto NA LISTA (ativa / apenas fiscal) sem abrir a filial.
  // Reenvia o objeto inteiro (FilialIn) com o campo trocado — certificado/logo não estão
  // no schema, então não são afetados.
  const toggleCampo = useMutation({
    mutationFn: async ({ f, campo, valor }: { f: Filial; campo: 'ativo' | 'somente_fiscal'; valor: boolean }) => {
      const payload: any = { ...f, [campo]: valor }
      delete payload.id; delete payload.empresa_id
      return (await api.put(`/filiais/${f.id}`, payload)).data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['filiais-admin'] })
      qc.invalidateQueries({ queryKey: ['filiais-minhas'] })
      qc.invalidateQueries({ queryKey: ['filiais-fiscais'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao atualizar'),
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-ink-900 flex items-center gap-2">
            <Building2 size={20} className="text-brand-600" /> Filiais
          </h2>
          <p className="text-sm text-ink-500 mt-0.5">
            Cada filial tem CNPJ próprio, série de NFe e usuários vinculados.
            Empresas sem filial seguem operando normalmente sem precisar cadastrar.
          </p>
        </div>
        <button className="btn-primary" onClick={() => { setEditing({ ...VAZIA }); setTab('geral') }}>
          <Plus size={14} /> Nova filial
        </button>
      </div>

      {filiais.length === 0 && !isLoading && (
        <div className="card card-body bg-amber-50 border border-amber-200 text-sm text-amber-900">
          <strong>Sem filiais cadastradas.</strong> O ERP está operando em modo "empresa única".
          Crie a primeira filial (marque como matriz) para começar a usar a estrutura multi-filial.
        </div>
      )}

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-ink-50 border-b border-ink-200">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-ink-700">Código</th>
              <th className="px-3 py-2 text-left font-medium text-ink-700">Nome fantasia</th>
              <th className="px-3 py-2 text-left font-medium text-ink-700">CNPJ</th>
              <th className="px-3 py-2 text-left font-medium text-ink-700">Cidade/UF</th>
              <th className="px-3 py-2 text-left font-medium text-ink-700">Tipo</th>
              <th className="px-3 py-2 text-center font-medium text-ink-700">Apenas fiscal</th>
              <th className="px-3 py-2 text-center font-medium text-ink-700">Ativa</th>
              <th className="w-24"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {isLoading && <tr><td colSpan={8} className="py-8 text-center text-ink-400"><Loader2 size={16} className="animate-spin inline" /> Carregando...</td></tr>}
            {!isLoading && filiais.length === 0 && (
              <tr><td colSpan={8} className="py-10 text-center text-ink-400">Nenhuma filial cadastrada.</td></tr>
            )}
            {filiais.map(f => (
              <tr key={f.id}
                onClick={() => { setEditing(f); setTab('geral') }}
                className={`hover:bg-violet-50 cursor-pointer ${!f.ativo ? 'opacity-60' : ''}`}>
                <td className="px-3 py-2 font-mono text-xs">{f.codigo}</td>
                <td className="px-3 py-2 font-medium">{f.nome_fantasia}</td>
                <td className="px-3 py-2 font-mono text-xs text-ink-600">{f.cnpj || '—'}</td>
                <td className="px-3 py-2 text-xs">{f.cidade ? `${f.cidade}/${f.uf}` : '—'}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1 flex-wrap">
                    {f.is_matriz
                      ? <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded inline-flex items-center gap-1"><Star size={10} /> Matriz</span>
                      : <span className="text-xs bg-ink-100 text-ink-700 px-2 py-0.5 rounded">Filial</span>}
                    {f.somente_fiscal && <span className="text-xs bg-brand-100 text-brand-800 px-2 py-0.5 rounded">Só fiscal</span>}
                    {f.perfil_operacao === 'industria' && <span className="text-xs bg-violet-100 text-violet-800 px-2 py-0.5 rounded">Indústria</span>}
                    {!f.ativo && <span className="text-xs bg-rose-100 text-rose-700 px-2 py-0.5 rounded">Inativa</span>}
                  </div>
                </td>
                {/* 30/06: toggles direto na lista (não some quando inativa/só-fiscal) */}
                <td className="px-3 py-2 text-center" onClick={e => e.stopPropagation()}>
                  <MiniToggle on={!!f.somente_fiscal} cor="brand"
                    title="Apenas fiscal — só emite nota, não opera/loga"
                    onChange={() => toggleCampo.mutate({ f, campo: 'somente_fiscal', valor: !f.somente_fiscal })} />
                </td>
                <td className="px-3 py-2 text-center" onClick={e => e.stopPropagation()}>
                  <MiniToggle on={!!f.ativo} cor="emerald"
                    title={f.ativo ? 'Ativa — clique p/ desativar' : 'Inativa — clique p/ ativar'}
                    onChange={() => toggleCampo.mutate({ f, campo: 'ativo', valor: !f.ativo })} />
                </td>
                <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center gap-1">
                    <button onClick={() => { setEditing(f); setTab('geral') }}
                      className="p-1.5 rounded hover:bg-brand-50 text-brand-700" title="Editar">
                      <Pencil size={13} />
                    </button>
                    <button onClick={() => { setEditing(f); setTab('usuarios') }}
                      className="p-1.5 rounded hover:bg-sky-50 text-sky-700" title="Usuários da filial">
                      <Users size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <FilialModal
          editing={editing}
          setEditing={setEditing}
          tab={tab}
          setTab={setTab}
          onSalvar={() => salvar.mutate(editing)}
          salvando={salvar.isPending}
        />
      )}
    </div>
  )
}

function FilialModal({
  editing, setEditing, tab, setTab, onSalvar, salvando,
}: {
  editing: Partial<Filial>
  setEditing: (f: Partial<Filial> | null) => void
  tab: 'geral' | 'fiscal' | 'usuarios'
  setTab: (t: 'geral' | 'fiscal' | 'usuarios') => void
  onSalvar: () => void
  salvando: boolean
}) {
  const set = (k: keyof Filial, v: any) => setEditing({ ...editing, [k]: v })

  return (
    <div className="fixed inset-0 bg-ink-950/70 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b border-ink-200 flex items-center justify-between flex-shrink-0">
          <div>
            <h3 className="text-lg font-bold text-ink-900">
              {editing.id ? `Filial: ${editing.nome_fantasia || editing.codigo}` : 'Nova filial'}
            </h3>
            {editing.id && (
              <p className="text-xs text-ink-500">ID #{editing.id}</p>
            )}
          </div>
          <button onClick={() => setEditing(null)} className="p-2 rounded hover:bg-ink-100"><X size={18} /></button>
        </div>

        <div className="border-b border-ink-200 px-3 flex gap-1 flex-shrink-0" style={{ minHeight: 44 }}>
          <TabBtn active={tab === 'geral'} onClick={() => setTab('geral')} icon={MapPin}>Geral & Endereço</TabBtn>
          <TabBtn active={tab === 'fiscal'} onClick={() => setTab('fiscal')} icon={FileText}>Fiscal & NFe</TabBtn>
          {editing.id && <TabBtn active={tab === 'usuarios'} onClick={() => setTab('usuarios')} icon={Users}>Usuários</TabBtn>}
        </div>

        <div className="flex-1 overflow-auto p-5 space-y-4">
          {tab === 'geral' && (
            <>
              {editing.id
                ? <LogoFilial filialId={editing.id} />
                : <div className="text-xs text-ink-500 italic mb-1">Salve a filial primeiro para enviar a logo dela.</div>}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="label">Código *</label>
                  <input className="input" placeholder="001" value={editing.codigo || ''}
                    onChange={e => set('codigo', e.target.value)} />
                </div>
                <div className="col-span-2">
                  <label className="label">CNPJ</label>
                  <input className="input font-mono" placeholder="00.000.000/0001-00" value={editing.cnpj || ''}
                    onChange={e => set('cnpj', e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Razão social *</label>
                  <input className="input" value={editing.razao_social || ''}
                    onChange={e => set('razao_social', e.target.value)} />
                </div>
                <div>
                  <label className="label">Nome fantasia *</label>
                  <input className="input" value={editing.nome_fantasia || ''}
                    onChange={e => set('nome_fantasia', e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="label">IE</label>
                  <input className="input" value={editing.ie || ''} onChange={e => set('ie', e.target.value)} />
                </div>
                <div>
                  <label className="label">IM</label>
                  <input className="input" value={editing.im || ''} onChange={e => set('im', e.target.value)} />
                </div>
                <div className="flex items-end gap-3">
                  <label className="flex items-center gap-2 text-sm pb-2">
                    <input type="checkbox" checked={!!editing.is_matriz}
                      onChange={e => set('is_matriz', e.target.checked)} />
                    <Star size={13} className="text-amber-500" /> Matriz
                  </label>
                  {/* 30/06: Ativa e Apenas fiscal viraram TOGGLES (pedido do usuário) */}
                  {(() => { const on = editing.ativo ?? true; return (
                    <button type="button" onClick={() => set('ativo', !on)} className="flex items-center gap-2 text-sm pb-2">
                      <div className={`w-10 h-6 rounded-full p-0.5 transition ${on ? 'bg-emerald-500' : 'bg-ink-300'}`}>
                        <div className={`w-5 h-5 rounded-full bg-white transition-transform ${on ? 'translate-x-4' : ''}`} />
                      </div>
                      Ativa
                    </button>
                  ) })()}
                  {/* FEATURE 30/06: filial só pra emitir nota (não loga/opera/relatórios) */}
                  {(() => { const on = !!editing.somente_fiscal; return (
                    <button type="button" onClick={() => set('somente_fiscal', !on)}
                      title="Filial usada SÓ como CNPJ emissor de nota no PDV. Não aparece pra logar nem nos relatórios de venda."
                      className="flex items-center gap-2 text-sm pb-2">
                      <div className={`w-10 h-6 rounded-full p-0.5 transition ${on ? 'bg-brand-600' : 'bg-ink-300'}`}>
                        <div className={`w-5 h-5 rounded-full bg-white transition-transform ${on ? 'translate-x-4' : ''}`} />
                      </div>
                      Apenas fiscal
                    </button>
                  ) })()}
                </div>
              </div>

              <div className="border-t border-ink-200 pt-3">
                <h4 className="text-sm font-semibold text-ink-900 mb-2">Endereço</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="label">CEP</label>
                    <input className="input" value={editing.cep || ''} onChange={e => set('cep', e.target.value)} />
                  </div>
                  <div className="col-span-2">
                    <label className="label">Endereço</label>
                    <input className="input" value={editing.endereco || ''}
                      onChange={e => set('endereco', e.target.value)} />
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-3 mt-3">
                  <div>
                    <label className="label">Número</label>
                    <input className="input" value={editing.numero || ''} onChange={e => set('numero', e.target.value)} />
                  </div>
                  <div>
                    <label className="label">Complemento</label>
                    <input className="input" value={editing.complemento || ''}
                      onChange={e => set('complemento', e.target.value)} />
                  </div>
                  <div>
                    <label className="label">Bairro</label>
                    <input className="input" value={editing.bairro || ''} onChange={e => set('bairro', e.target.value)} />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="col-span-2">
                      <label className="label">Cidade</label>
                      <input className="input" value={editing.cidade || ''}
                        onChange={e => set('cidade', e.target.value)} />
                    </div>
                    <div>
                      <label className="label">UF</label>
                      <input className="input" maxLength={2} value={editing.uf || ''}
                        onChange={e => set('uf', e.target.value.toUpperCase())} />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <label className="label">Telefone</label>
                    <input className="input" value={editing.telefone || ''}
                      onChange={e => set('telefone', e.target.value)} />
                  </div>
                  <div>
                    <label className="label">E-mail</label>
                    <input className="input" type="email" value={editing.email || ''}
                      onChange={e => set('email', e.target.value)} />
                  </div>
                </div>
              </div>
            </>
          )}

          {tab === 'fiscal' && (
            <>
              <div className="bg-sky-50 border border-sky-200 rounded p-3 text-xs text-sky-900 flex items-start gap-2">
                <ShieldCheck size={14} className="mt-0.5 shrink-0" />
                <div>Campos vazios herdam da configuração da empresa. Preencha apenas o que for específico desta filial (série própria, certificado dedicado, CNAE diferente, etc).</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Regime tributário</label>
                  <select className="input" value={editing.regime_tributario || ''}
                    onChange={e => set('regime_tributario', e.target.value || null)}>
                    <option value="">— herda da empresa —</option>
                    <option value="simples">Simples Nacional</option>
                    <option value="lucro_presumido">Lucro Presumido</option>
                    <option value="lucro_real">Lucro Real</option>
                    <option value="mei">MEI</option>
                  </select>
                </div>
                <div>
                  <label className="label">CRT</label>
                  <select className="input" value={editing.crt || ''}
                    onChange={e => set('crt', e.target.value || null)}>
                    <option value="">— herda da empresa —</option>
                    <option value="1">1 — Simples Nacional</option>
                    <option value="2">2 — Simples (excesso sublimite)</option>
                    <option value="3">3 — Regime Normal</option>
                    <option value="4">4 — MEI</option>
                  </select>
                </div>
              </div>
              {/* Perfil da operação — define COMO o kit sai na nota deste CNPJ.
                  INDÚSTRIA: 1 item só (a bike montada). LOJA: item a item (componentes).
                  Só afeta a montagem do XML — pedido, estoque e financeiro continuam
                  registrando os componentes normalmente nos dois casos. */}
              <div>
                <label className="label">Perfil da operação (kit na NF-e)</label>
                <select className="input" value={editing.perfil_operacao || 'loja'}
                  onChange={e => set('perfil_operacao', e.target.value)}>
                  <option value="loja">Loja — kit sai item a item (componentes)</option>
                  <option value="industria">Indústria — kit sai como produto único (a bike)</option>
                </select>
                <p className="text-xs text-ink-500 mt-1">
                  {editing.perfil_operacao === 'industria'
                    ? 'A nota deste CNPJ mostra só a bike montada. O produto kit precisa ter NCM, CFOP, CST/CSOSN e origem preenchidos no cadastro — a emissão acusa o que estiver faltando.'
                    : 'A nota deste CNPJ discrimina cada componente do kit. É o comportamento padrão.'}
                </p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="label">Ambiente NFe</label>
                  <select className="input" value={editing.ambiente_nfe || ''}
                    onChange={e => set('ambiente_nfe', e.target.value || null)}>
                    <option value="">— herda —</option>
                    <option value="1">1 — Produção</option>
                    <option value="2">2 — Homologação</option>
                  </select>
                </div>
                <div>
                  <label className="label">Série NFe</label>
                  <input className="input" placeholder="1" value={editing.serie_nfe || ''}
                    onChange={e => set('serie_nfe', e.target.value || null)} />
                </div>
                <div>
                  <label className="label">Série NFCe</label>
                  <input className="input" placeholder="1" value={editing.serie_nfce || ''}
                    onChange={e => set('serie_nfce', e.target.value || null)} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="label">Próximo nº NFe</label>
                  <input className="input" type="number" value={editing.nfe_proxima_numero || ''}
                    onChange={e => set('nfe_proxima_numero', e.target.value ? parseInt(e.target.value) : null)} />
                </div>
                <div>
                  <label className="label">CSC ID</label>
                  <input className="input" value={editing.csc_id || ''}
                    onChange={e => set('csc_id', e.target.value || null)} />
                </div>
                <div>
                  <label className="label">CSC Token</label>
                  <input className="input" type="password" value={editing.csc_token || ''}
                    onChange={e => set('csc_token', e.target.value || null)} />
                </div>
              </div>
              {editing.id
                ? <CertificadoFilial filialId={editing.id} cnpjFilial={editing.cnpj || ''} />
                : <div className="text-xs text-ink-500 italic">Salve a filial primeiro para enviar o certificado A1 dela.</div>}
            </>
          )}

          {tab === 'usuarios' && editing.id && (
            <UsuariosFilial filialId={editing.id} />
          )}
        </div>

        <div className="px-5 py-3 bg-ink-50 border-t border-ink-200 flex justify-end gap-2 flex-shrink-0">
          <button className="btn-secondary" onClick={() => setEditing(null)}>Cancelar</button>
          {tab !== 'usuarios' && (
            <button className="btn-primary" onClick={onSalvar}
              disabled={salvando || !editing.codigo || !editing.razao_social || !editing.nome_fantasia}>
              {salvando ? <Loader2 size={14} className="animate-spin" /> : 'Salvar'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function TabBtn({ active, onClick, icon: Icon, children }: { active: boolean; onClick: () => void; icon: any; children: React.ReactNode }) {
  return (
    <button onClick={onClick}
      className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
        active ? 'border-brand-500 text-brand-700' : 'border-transparent text-ink-600 hover:text-ink-900'
      }`}>
      <Icon size={13} /> {children}
    </button>
  )
}

function UsuariosFilial({ filialId }: { filialId: number }) {
  const qc = useQueryClient()
  const [novoUserId, setNovoUserId] = useState<string>('')
  const [novoPapel, setNovoPapel] = useState<string>('operador')

  const { data: vinculos = [] } = useQuery<any[]>({
    queryKey: ['filial-usuarios', filialId],
    queryFn: async () => (await api.get(`/filiais/${filialId}/usuarios`)).data,
  })
  const { data: usuariosTodos = [] } = useQuery<any[]>({
    queryKey: ['usuarios-empresa'],
    queryFn: async () => (await api.get('/auth/usuarios')).data,
  })

  const idsVinculados = new Set(vinculos.map(v => v.usuario_id))
  const disponiveis = usuariosTodos.filter((u: any) => !idsVinculados.has(u.id))

  const adicionar = useMutation({
    mutationFn: async () => (await api.post('/filiais/vinculos', {
      usuario_id: parseInt(novoUserId), filial_id: filialId, papel: novoPapel,
    })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['filial-usuarios', filialId] })
      setNovoUserId(''); setNovoPapel('operador')
      toast.success('Usuário vinculado')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  const remover = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/filiais/vinculos/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['filial-usuarios', filialId] })
      toast.success('Vínculo removido')
    },
  })

  const setarDefault = useMutation({
    mutationFn: async (id: number) => (await api.put(`/filiais/vinculos/${id}/default`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['filial-usuarios', filialId] })
      toast.success('Filial default atualizada')
    },
  })

  return (
    <div className="space-y-4">
      <div className="bg-ink-50 rounded p-3">
        <h4 className="text-sm font-medium mb-2">Vincular usuário</h4>
        <div className="flex gap-2">
          <select className="input flex-1" value={novoUserId} onChange={e => setNovoUserId(e.target.value)}>
            <option value="">Selecione um usuário...</option>
            {disponiveis.map((u: any) => <option key={u.id} value={u.id}>{u.nome} ({u.email})</option>)}
          </select>
          <select className="input w-40" value={novoPapel} onChange={e => setNovoPapel(e.target.value)}>
            <option value="operador">Operador</option>
            <option value="gestor">Gestor</option>
            <option value="admin">Admin</option>
          </select>
          <button className="btn-primary" disabled={!novoUserId || adicionar.isPending}
            onClick={() => adicionar.mutate()}>
            {adicionar.isPending ? <Loader2 size={14} className="animate-spin" /> : 'Vincular'}
          </button>
        </div>
      </div>

      {vinculos.length === 0 ? (
        <div className="text-sm text-ink-500 italic text-center py-6">
          Nenhum usuário vinculado. Sem vínculos, todos os usuários da empresa enxergam todas as filiais.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="border-b border-ink-200 text-xs text-ink-600">
            <tr>
              <th className="text-left py-2">Usuário</th>
              <th className="text-left py-2">Papel</th>
              <th className="text-center py-2">Default</th>
              <th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {vinculos.map((v: any) => (
              <tr key={v.id}>
                <td className="py-2">
                  <div className="font-medium">{v.nome}</div>
                  <div className="text-xs text-ink-500">{v.email}</div>
                </td>
                <td className="py-2 capitalize">{v.papel}</td>
                <td className="py-2 text-center">
                  {v.is_default
                    ? <Star size={14} className="text-amber-500 fill-amber-500 inline" />
                    : <button onClick={() => setarDefault.mutate(v.id)}
                        className="text-xs text-ink-400 hover:text-amber-600">marcar default</button>}
                </td>
                <td className="py-2 text-right">
                  <button onClick={() => pedirExclusao({ descricao: 'o vínculo de filial', onConfirm: () => remover.mutate(v.id) })}
                    className="p-1 rounded hover:bg-rose-50 text-rose-600">
                    <Trash2 size={13} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}


function LogoFilial({ filialId }: { filialId: number }) {
  const qc = useQueryClient()
  const { data } = useQuery<any>({
    queryKey: ['logo-filial', filialId],
    queryFn: async () => (await api.get(`/filiais/${filialId}/logo`)).data,
  })
  const logo = data?.logo_data || null
  const enviar = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData()
      fd.append('file', file)
      return (await api.post(`/filiais/${filialId}/logo`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } })).data
    },
    onSuccess: () => { toast.success('Logo da filial atualizada'); qc.invalidateQueries({ queryKey: ['logo-filial', filialId] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao enviar a logo'),
  })
  const remover = useMutation({
    mutationFn: async () => (await api.delete(`/filiais/${filialId}/logo`)).data,
    onSuccess: () => { toast.success('Logo removida'); qc.invalidateQueries({ queryKey: ['logo-filial', filialId] }) },
  })
  return (
    <div className="border border-ink-200 rounded-lg p-3 flex items-center gap-4">
      <div className="h-16 w-16 rounded-lg border border-ink-200 bg-ink-50 flex items-center justify-center overflow-hidden shrink-0">
        {logo ? <img src={logo} alt="logo" className="h-full w-full object-contain" />
              : <ImageIcon size={22} className="text-ink-300" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-ink-700 flex items-center gap-2"><ImageIcon size={15} /> Logo da filial</div>
        <p className="text-xs text-ink-500 mb-2">Aparece no topo do sistema, nas OS, relatórios e DANFE quando esta filial está selecionada. PNG/JPG/WEBP, até 2MB.</p>
        <div className="flex items-center gap-2">
          <label className="btn-secondary text-xs cursor-pointer">
            {enviar.isPending ? 'Enviando...' : (logo ? 'Trocar' : 'Enviar logo')}
            <input type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden"
              onChange={e => { const f = e.target.files?.[0]; if (f) enviar.mutate(f); e.currentTarget.value = '' }} />
          </label>
          {logo && (
            <button className="btn-secondary text-xs flex items-center gap-1 text-red-600" onClick={() => pedirExclusao({ descricao: 'a logo da filial', onConfirm: () => remover.mutate() })}>
              <Trash size={13} /> Remover
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function CertificadoFilial({ filialId, cnpjFilial }: { filialId: number; cnpjFilial: string }) {
  const qc = useQueryClient()
  const [file, setFile] = useState<File | null>(null)
  const [senha, setSenha] = useState('')
  const { data: status } = useQuery<any>({
    queryKey: ['cert-filial', filialId],
    queryFn: async () => (await api.get(`/filiais/${filialId}/certificado`)).data,
  })
  const enviar = useMutation({
    mutationFn: async () => {
      const fd = new FormData()
      fd.append('file', file as File)
      fd.append('senha', senha)
      return (await api.post(`/filiais/${filialId}/certificado`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } })).data
    },
    onSuccess: () => { toast.success('Certificado da filial enviado'); setFile(null); setSenha(''); qc.invalidateQueries({ queryKey: ['cert-filial', filialId] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro no certificado'),
  })
  return (
    <div className="border border-ink-200 rounded-lg p-3 space-y-2">
      <div className="flex items-center gap-2 text-sm font-semibold text-ink-700">
        <ShieldCheck size={15} /> Certificado A1 da filial (CNPJ {cnpjFilial || '—'})
      </div>
      {status?.configurado ? (
        <div className="text-xs text-emerald-700">
          ✓ {status.cn} — CNPJ {status.cnpj} — válido até {status.valido_ate ? new Date(status.valido_ate).toLocaleDateString('pt-BR') : '?'}
          {status.dias_restantes != null && <span className="text-ink-500"> ({status.dias_restantes} dias)</span>}
        </div>
      ) : (
        <div className="text-xs text-amber-600">Nenhum certificado enviado — a filial não emite NF sem ele.</div>
      )}
      <div className="grid grid-cols-3 gap-2 items-end">
        <div className="col-span-1">
          <label className="label">Arquivo .pfx/.p12</label>
          <input type="file" accept=".pfx,.p12" className="text-xs"
            onChange={e => setFile(e.target.files?.[0] || null)} />
        </div>
        <div>
          <label className="label">Senha</label>
          <input type="password" className="input" value={senha} onChange={e => setSenha(e.target.value)}
            autoComplete="new-password" name="cert-senha-filial" data-lpignore="true" data-1p-ignore />
        </div>
        <button className="btn-primary" disabled={!file || !senha || enviar.isPending}
          onClick={() => enviar.mutate()}>
          {enviar.isPending ? 'Enviando...' : (status?.configurado ? 'Substituir' : 'Enviar')}
        </button>
      </div>
    </div>
  )
}
